import { isAxiosError } from "axios";

/**
 * Framework-free error helpers shared by every api/*.ts and services/*.ts module.
 * Deliberately has NO import from "@/contexts/AxiosContext" (which pulls in React +
 * okta-react) so modules like services/entityOpsV2.ts that must stay unit-testable
 * against a fake AxiosInstance (no React) can depend on this file safely.
 */

interface ApiErrorResponse {
  readonly error?: string;
  readonly message?: string;
}

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (isAxiosError<ApiErrorResponse>(error)) {
    const data = error.response?.data;

    if (data?.error) {
      return data.error;
    }

    if (data?.message) {
      return data.message;
    }

    return error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};

/**
 * Thrown for HTTP 423 (Locked) responses: the project's owner has locked it for editing,
 * and the current user is not the owner. Distinct from a generic 403 (permission denied)
 * and 409 (`ProjectSaveConflictError`, concurrent edit) so callers can react specifically -
 * e.g. refresh the project's lock state and show a "this project is locked" message instead
 * of a generic failure toast. Also kept out of the 401/403 bucket so the Axios interceptor's
 * token-refresh-on-403 logic (see AxiosContext) never fires for this case.
 */
export class ProjectLockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ProjectLockedError";
  }
}

/** True when `error` is an Axios error with HTTP status 423 (project locked by its owner). */
export const isProjectLockedError = (error: unknown): boolean => isAxiosError<ApiErrorResponse>(error) && error.response?.status === 423;

/**
 * Wraps a write call so a 423 response surfaces as `ProjectLockedError` instead of a
 * generic `Error`. Use this around every mutationFn that hits an owner-lock-gated
 * endpoint (image/video/text generation, mutable entity writes, workflow starts,
 * uploads, project save/archive/restore/delete/thumbnail, v2 entity/meta writes).
 */
export const throwLockAwareError = (error: unknown, fallback: string): never => {
  if (isProjectLockedError(error)) {
    throw new ProjectLockedError(getErrorMessage(error, "This project is locked by its owner."));
  }

  throw new Error(getErrorMessage(error, fallback));
};
